/**
 * Maps form step IDs to field-permission JSON section names.
 * Single source of truth for `useStepVisibility` and any synchronous step checks
 * (e.g. basic/advanced view navigation in RequestDetail).
 */

const BASE_FORM_STEP_ID_TO_SECTIONS: Readonly<
  Record<string, readonly string[]>
> = {
  opportunity: ['Opportunity Information'],
  /** Physical site + UOM (RBAC: Site Dimensions) */
  'site-information': ['Site Dimensions'],
  racks: ['Racks'],
  'facility-power': ['Facility Power Readiness'],
  /** #1330 — scoped Facility Power cards share the same RBAC JSON section. */
  'large-ups': ['Facility Power Readiness'],
  'power-distribution': ['Facility Power Readiness'],
  'rack-power': ['Rack Power'],
  'small-ups': ['Small UPS'],
  'cooling-readiness': ['Cooling Readiness'],
  'liquid-cooling': ['Liquid Cooling'],
  'customer-it': ['Customer IT Equipment'],
  monitoring: ['Monitoring Management'],
  documents: ['Supporting Documentation'],
  feedback: ['Feedback'],
  /** Summary does not use section-based visibility. */
  summary: [],
} as const;

/**
 * Step → RBAC sections. When Project Details is on, Stream 2 rack fields use JSON section `Racks`
 * but the sidebar exposes **`rack-power`** only — merge `Racks` into that step for visibility.
 */
export function getFormStepIdToSections(): Record<string, readonly string[]> {
  const withoutRacksAndCustomerIt = Object.fromEntries(
    Object.entries(BASE_FORM_STEP_ID_TO_SECTIONS).filter(
      ([stepId]) =>
        stepId !== 'racks' &&
        stepId !== 'customer-it' &&
        stepId !== 'monitoring',
    ),
  ) as Record<string, readonly string[]>;
  return {
    ...withoutRacksAndCustomerIt,
    'rack-power': ['Rack Power', 'Racks'] as const,
  };
}

/** Legacy static map (Stream 1 shape). Prefer {@link getFormStepIdToSections} when RBAC must match the active flow. */
export const FORM_STEP_ID_TO_SECTIONS = BASE_FORM_STEP_ID_TO_SECTIONS;
